import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import GenerateDialog from './generate-dialog';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import ErrorIcon from '@components-clean/core/icons/error-icon';
import InformationIcon from '@components-clean/core/icons/information-icon';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import SearchableListBox from '@components-clean/core/searchable-list-box';
import {
    ControlsAutomationColorEnumConvert,
    StudyAutomationColorEnumConvert,
} from '@interfaces-clean/study-color-automation.interface';
import IHubDataset, {
    AutomationTypes,
} from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import RevertDialog from '@pages-clean/central-monitoring/dataset-hub/tabs/automation/revert-dialog';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromUtcNonNullable } from '@utils/convertor';
import { GET_STUDY_AUTOMATION_CONFIG } from '@queries/central-monitoring';
import {
    getStudyAutomationConfig,
    getStudyAutomationConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

interface IProps {
    studyRef: string;
    dataset?: IHubDataset;
    automationType: AutomationTypes;
}
interface HeaderAction {
    type: 'Generate' | 'Revert' | 'AutomationDisabled';
    isDisabled: boolean;
}

const Automation = ({ studyRef, dataset, automationType }: IProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('cm', {
        keyPrefix: 'datasetHub.tabs.automation',
    });
    const { t: tAdmin } = useTranslation('admin');
    const user = useAuthorization();
    if (!user) throw new Error('User not found');

    const {
        data,
        isLoading: isLoadingAutomation,
        error: datasetError,
    } = DatasetApi.useGetAutomation(studyRef, dataset?.id, automationType);
    const {
        data: allDatasets,
        isLoading: isLoadingAllDatasets,
        error: allDatasetsError,
    } = DatasetApi.useGetGrid(studyRef, automationType);
    const { data: liveConfig, loading: isLoadingLiveConfig } = useQuery<
        getStudyAutomationConfig,
        getStudyAutomationConfigVariables
    >(GET_STUDY_AUTOMATION_CONFIG, {
        variables: { studyId: studyRef },
    });

    const {
        data: configAsOfAutomation,
        loading: isLoadingConfigAsOfAutomation,
        error: configAsOfAutomationError,
    } = useQuery<getStudyAutomationConfig, getStudyAutomationConfigVariables>(GET_STUDY_AUTOMATION_CONFIG, {
        variables: { studyId: studyRef, timestamp: data?.reviewGeneratedOn },
    });

    const isLoading =
        isLoadingLiveConfig || isLoadingConfigAsOfAutomation || isLoadingAutomation || isLoadingAllDatasets;
    const generalError = datasetError || allDatasetsError;

    const selectedDataset = useMemo(() => {
        if (!allDatasets || !dataset) return null;

        return allDatasets.find((d) => d.id === dataset.id);
    }, [allDatasets, dataset]);

    const headerActionContentType = useMemo((): HeaderAction | undefined => {
        if (!allDatasets) return;

        if (!selectedDataset) return;

        const hasWritePermission = user.canWrite?.({
            module: Constants.Modules.CM2,
            element: Constants.DataElements.CentralMonitoringDefault,
            permission: Permissions.ReadWrite,
        });

        if (!hasWritePermission) {
            return;
        }

        if (automationType === AutomationTypes.Site && !liveConfig?.getStudyAutomationConfig?.isAutomationEnabled)
            return { type: 'AutomationDisabled', isDisabled: true };

        if (
            automationType === AutomationTypes.Controls &&
            !liveConfig?.getStudyAutomationConfig?.isControlsAutomationEnabled
        )
            return { type: 'AutomationDisabled', isDisabled: true };

        const hasRunAutomation = selectedDataset.automation && selectedDataset.automation.status === 'Successful';
        let isDisabled = false;

        if (selectedDataset.automation?.status === 'Running' || selectedDataset.automation?.status === 'Reverting') {
            isDisabled = true;
        }

        const datasetsWitRunningOrRevertingAutomations = allDatasets.filter(
            (d) => d.automation && (d.automation.status === 'Running' || d.automation.status === 'Reverting'),
        );

        const datasetsWithSuccessfulAutomations = allDatasets.filter(
            (d) => d.automation && d.date > selectedDataset.date && d.automation.status === 'Successful',
        );

        if (datasetsWitRunningOrRevertingAutomations.length !== 0 || datasetsWithSuccessfulAutomations.length !== 0) {
            isDisabled = true;
        }

        const userPermissions = user?.isObsAndLogsAdmin();
        return hasRunAutomation
            ? { type: 'Revert', isDisabled: isDisabled || !userPermissions }
            : { type: 'Generate', isDisabled };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [liveConfig, allDatasets, selectedDataset]);

    let infoMessage;

    if (!dataset) {
        infoMessage = tRoot('cm:datasetHub:selectRecord');
    } else if (!isLoading && !data) {
        infoMessage = t('noAutomationRun');
    }

    let content;

    if (generalError) {
        content = (
            <div className="no-data-content">
                <InfoField type="Error" message={t('errors.generalLoad')} layout="center" />
            </div>
        );
    } else if (infoMessage) {
        content = (
            <div className="no-data-content">
                <InfoField type="Info" message={infoMessage} layout="center" />
            </div>
        );
    } else if (data) {
        content = (
            <>
                <dl>
                    <dt>{t('labels.automationStatus')}</dt>
                    <dd>{data?.status}</dd>

                    <dt>{t('labels.automationTrigger')}</dt>
                    {automationType === AutomationTypes.Site && (
                        <dd>{tAdmin(`${StudyAutomationColorEnumConvert(data?.siteTrigger)}`)}</dd>
                    )}
                    {automationType === AutomationTypes.Controls && (
                        <dd>{tAdmin(`${ControlsAutomationColorEnumConvert(data?.controlsTrigger)}`)}</dd>
                    )}

                    <dt>{t('labels.numberOfGeneratedObservations')}</dt>
                    <dd>{data?.generatedObservationCount}</dd>

                    <dt>{t('labels.numberOfGeneratedReviewLogs')}</dt>
                    <dd>{data?.generatedReviewLogCount}</dd>
                </dl>

                <div className="lists-area">
                    {automationType === AutomationTypes.Site && (
                        <Card title={t('listBoxTitles.excludedStatuses')} data-testid="excluded-states-card">
                            {configAsOfAutomationError ? (
                                <InfoField type="Error" message={t('errors.configLoad')} layout="center" />
                            ) : (
                                <SearchableListBox
                                    data={
                                        configAsOfAutomation?.getStudyAutomationConfig.excludedSiteStatuses.map(
                                            (status) => ({
                                                name: status,
                                            }),
                                        ) ?? []
                                    }
                                    textField="name"
                                />
                            )}
                        </Card>
                    )}

                    {automationType === AutomationTypes.Site && (
                        <Card title={t('listBoxTitles.excludedSites')} data-testid="excluded-sites-card">
                            <SearchableListBox
                                data={
                                    data?.excludedSites?.map((site) => ({
                                        name: `${site.name}${site.status && ` : ${site.status}`}`,
                                    })) ?? []
                                }
                                textField="name"
                            />
                        </Card>
                    )}

                    <Card title={t('listBoxTitles.excludedPanels')} data-testid="excluded-panels-card">
                        <SearchableListBox
                            data={data?.excludedPanels?.map((panel) => ({ name: panel.name })) ?? []}
                            textField="name"
                        />
                    </Card>
                </div>
            </>
        );
    }

    const [showGenerateDialog, setShowGenerateDialog] = React.useState(false);
    const showRevertDialog = useBoolean(false);

    const getHeaderActionContent = () => {
        if (configAsOfAutomationError) {
            return (
                <ErrorIcon
                    dataTestId="config-error-icon"
                    title={t('errors.configLoad')}
                    tooltipPosition="left"
                    className="k-pr-1"
                />
            );
        }

        switch (headerActionContentType?.type) {
            case 'Generate':
                return (
                    <Button
                        primary
                        data-testid="generate"
                        onClick={() => setShowGenerateDialog(true)}
                        disabled={headerActionContentType?.isDisabled}
                    >
                        {t('labels.generateButton')}
                    </Button>
                );
            case 'Revert':
                return (
                    <Button
                        primary
                        data-testid="revert"
                        onClick={showRevertDialog.setTrue}
                        disabled={headerActionContentType?.isDisabled}
                    >
                        {t('labels.revertButton')}
                    </Button>
                );
            case 'AutomationDisabled':
                return (
                    <InformationIcon
                        dataTestId="automation-disabled-icon"
                        title={automationType === AutomationTypes.Site ? t('notEnabled') : t('controlsNotEnabled')}
                        tooltipPosition="left"
                        className="k-pr-1"
                    />
                );
            default:
                return undefined;
        }
    };

    return (
        <>
            <Card
                title={`Dataset: ${stringDateWithoutTimeFromUtcNonNullable(dataset?.date, 'DD-MMM-YYYY')}`}
                className="automation-card"
                data-testid="automation-card"
                headerActions={!IsPopOut() && getHeaderActionContent()}
            >
                {content}

                <div className={`loading-overlay ${!isLoading ? 'fade-out' : ''}`}>
                    <Loading />
                </div>
            </Card>
            {showGenerateDialog && (
                <GenerateDialog
                    dataset={selectedDataset!}
                    studyRef={studyRef}
                    onClose={() => setShowGenerateDialog(false)}
                    automationType={automationType}
                />
            )}
            {showRevertDialog.value && data && (
                <RevertDialog
                    dataset={selectedDataset!}
                    studyRef={studyRef}
                    onClose={showRevertDialog.setFalse}
                    automation={data}
                    automationType={automationType}
                />
            )}
        </>
    );
};

export default Automation;
